import React, { ReactNode, useCallback, useState } from 'react';
import { Box, FormHelperText, Grid } from '@mui/material';
import { Accept, DropEvent, useDropzone } from 'react-dropzone';
import { styled as materialStyled, styled } from '@mui/material/styles';
import { t } from 'i18next';
import { StyledBadge } from './UploadProfilePicture';

const FormHelperTextStyled = materialStyled(FormHelperText)(() => ({
  fontSize: '14px',
  fontWeight: 400,
  fontFamily: 'Satoshi',
  variant: 'filled',
}));

const StyledGrid = styled(Grid)({
  height: 200,
  width: 200,
  borderRadius: 10,
  border: '1px dashed #7CAEBE',
});
interface CustomDropzoneProps {
  accept: Accept;
  maxFiles?: number;
  dargActiveText: ReactNode;
  dragInactiveText: ReactNode;
  invalidFileTypeError: string;
  onDropAccepted?: <T extends File>(_files: T[], _event: DropEvent) => void;
  fileDetails?: ReactNode;
  fileDetailsAction?: ReactNode;
  maxSize?: number;
  handleDelteFiles: () => void;
  acceptedFile: string;
}
interface FileError {
  code: string;
  message: string;
}

interface RejectedFile {
  file: File;
  errors: FileError[];
}

function CustomDropzone({
  accept,
  maxFiles,
  dargActiveText,
  dragInactiveText,
  invalidFileTypeError,
  onDropAccepted,
  maxSize,
  handleDelteFiles,
  acceptedFile,
}: CustomDropzoneProps) {
  const [error, setError] = useState('');

  const onDropRejected = useCallback((rejected: RejectedFile[]) => {
    const er = rejected[0].errors[0].code;
    if (er === 'file-invalid-type') {
      setError(invalidFileTypeError);
    }
    if (er === 'file-too-large') {
      setError(t('error.fileUpload.fileSizeTooLarge', { maxSize: '10' }));
    }
  }, []);

  const onDrop = useCallback(() => {
    setError('');
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    onDrop,
    accept,
    maxFiles,
    maxSize,
  });

  const removeFiles = () => {
    handleDelteFiles();
  };

  return (
    <div>
      {['', 'null', null].includes(acceptedFile) ? (
        <div {...getRootProps()}>
          <input data-testid="dropzone" {...getInputProps()} />
          <StyledGrid container justifyContent="center" alignContent="center">
            {isDragActive ? dargActiveText : dragInactiveText}
          </StyledGrid>
        </div>
      ) : (
        <StyledBadge
          badgeContent={
            <Box onClick={removeFiles} sx={{ cursor: 'pointer' }}>
              <img src="/assets/icons/edit.svg" alt="tick" />
            </Box>
          }
        >
          <Box component="img" src={acceptedFile} height={200} width={200} borderRadius="10px" />
        </StyledBadge>
      )}
      <FormHelperTextStyled error>{error}</FormHelperTextStyled>
    </div>
  );
}

export default CustomDropzone;
